import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import posthog from 'posthog-js';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const extractApiError = (error) => {
    return (
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.response?.data?.Erro ||
      error.message
    );
  };

  // aplicarPerfilAtualizado busca o perfil na API e substitui o que estava em cache — mesmo
  // formato de resposta do login, então serve tanto pro sync automático no carregamento quanto
  // pro refreshUser() manual (ex.: a tela de Segurança depois de ativar/desativar 2FA).
  const aplicarPerfilAtualizado = async (userId, usuarioBase) => {
    try {
      const response = await api.get(`/user/profile/${userId}`);
      const userPayload = { ...usuarioBase, ...response.data };
      localStorage.setItem('user', JSON.stringify(userPayload));
      setUser(userPayload);
      return userPayload;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      if (parsed?.id) {
        posthog.identify(String(parsed.id), { name: parsed.name });

        // A tela já pintou com o que tinha em cache — isso é só um sync silencioso em
        // background, pra pegar o que mudou no servidor desde o último login (e-mail
        // confirmado, 2FA ativado/desativado, etc.) sem precisar deslogar e logar de novo.
        aplicarPerfilAtualizado(parsed.id, parsed);
      }
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/user/login', { email, password });
      const data = response.data || {};

      // Com 2FA ativo, a senha correta não cria sessão — devolve o desafio. Quem chamou decide o
      // que fazer com isso (normalmente navegar para /login/2fa).
      if (data.two_factor_required) {
        return {
          twoFactorRequired: true,
          twoFactorToken: data.two_factor_token,
          expiresIn: data.expires_in,
        };
      }

      const { token, user: userPayload } = data;

      if (!token || !userPayload) {
        throw new Error('Login inválido. Tente novamente.');
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userPayload));
      setUser(userPayload);

      posthog.identify(String(userPayload.id), { name: userPayload.name });
      posthog.capture('user_logged_in');

      return userPayload;
    } catch (error) {
      // Login com a conta em lockout de 2FA (tentativa nova não escapa do lockout, que é por
      // conta) também vem com code/retryAfter — mesmo tratamento do verifyTwoFactor, pra quem
      // chama poder mostrar quanto tempo falta.
      const erroComDetalhes = new Error(extractApiError(error) || 'Email ou senha incorretos');
      erroComDetalhes.code = error.response?.data?.code;
      erroComDetalhes.retryAfterSeconds = error.response?.data?.retryAfter;
      throw erroComDetalhes;
    }
  };

  // verifyTwoFactor troca o desafio por sessão completa — segunda etapa do login. Devolve o
  // mesmo formato de login(): um userPayload de verdade, nunca outro desafio.
  const verifyTwoFactor = async (twoFactorToken, { code, recoveryCode } = {}) => {
    try {
      const response = await api.post('/security/2fa/verify', {
        two_factor_token: twoFactorToken,
        code: code || undefined,
        recovery_code: recoveryCode || undefined,
      });
      const { token, user: userPayload } = response.data || {};

      if (!token || !userPayload) {
        throw new Error('Verificação inválida. Tente novamente.');
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userPayload));
      setUser(userPayload);

      posthog.identify(String(userPayload.id), { name: userPayload.name });
      posthog.capture('user_logged_in', { two_factor: true });

      return userPayload;
    } catch (error) {
      // O BFF normaliza o nome do campo pra retryAfter (mesmo contrato do refuse de depósito) —
      // não é retry_after_seconds, que é só o nome que a API Go usa antes de passar pelo BFF.
      const erroComDetalhes = new Error(extractApiError(error) || 'Código inválido');
      erroComDetalhes.code = error.response?.data?.code;
      erroComDetalhes.retryAfterSeconds = error.response?.data?.retryAfter;
      erroComDetalhes.attemptsRemaining = error.response?.data?.attemptsRemaining;
      throw erroComDetalhes;
    }
  };

  // refreshUser relê o perfil sob demanda — a tela de Segurança usa isso depois de
  // ativar/desativar 2FA, pra não esperar o próximo carregamento de página.
  const refreshUser = async () => {
    if (!user?.id) return null;
    return aplicarPerfilAtualizado(user.id, user);
  };

  const register = async (email, password, ref, refcode) => {
    try {
      console.log('[Register] Iniciando criação de conta', { email: email?.toLowerCase(), hasRef: !!ref, hasRefcode: !!refcode });
      
      await api.post('/user/new', {
        email,
        password,
        ref: ref || '',
        refcode: refcode || '',
      });

      console.log('[Register] Conta criada com sucesso', { email: email?.toLowerCase() });

      const loginResponse = await api.post('/user/login', { email, password });
      const { token, user: userPayload } = loginResponse.data || {};

      if (token && userPayload) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userPayload));
        setUser(userPayload);
        console.log('[Register] Login automático realizado', { email: email?.toLowerCase(), userId: userPayload?.id });

        posthog.identify(String(userPayload.id), { name: userPayload.name });
        posthog.capture('user_signed_up', {
          has_ref: !!ref,
          has_refcode: !!refcode,
        });
      }

      return userPayload || null;
    } catch (error) {
      const status = error.response?.status;
      const apiMessage = extractApiError(error);
      
      console.error('[Register] Erro ao criar conta', {
        email: email?.toLowerCase(),
        status,
        message: apiMessage,
        error: error.message,
      });

      if (status === 401 || status === 409) {
        throw new Error('Este email já está cadastrado. Tente fazer login ou use outro email.');
      }

      if (apiMessage && apiMessage.toLowerCase().includes('email') && apiMessage.toLowerCase().includes('já')) {
        throw new Error('Este email já está cadastrado. Tente fazer login ou use outro email.');
      }

      throw new Error(apiMessage || 'Erro ao criar conta. Tente novamente.');
    }
  };

  const logout = async () => {
    try {
      posthog.reset();
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const forgotPassword = async (email) => {
    try {
      await api.post('/user/reset-password', { email });
      return true;
    } catch (error) {
      const apiMessage = error.response?.data?.message;
      throw new Error(apiMessage || extractApiError(error) || 'Erro ao enviar email');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        forgotPassword,
        verifyTwoFactor,
        refreshUser,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
